"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useComponent = void 0;
/* eslint-disable no-param-reassign */
const assert_never_1 = __importDefault(require("assert-never"));
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const spu_1 = require("@tencent/tccp-frontend-contract/spu");
const useStatic_1 = require("../_hooks/useStatic");
const useSubject_1 = require("../_hooks/useSubject");
const compiler_1 = require("../modules/compiler");
const component_1 = require("../modules/component");
const component_2 = require("../types/component");
function useComponent({ spuMeta_, paramList_, strategyList_, validator_, values_, loaded_ }, { setCurrentValues, setDefaultValues, setParamBehavior }) {
    const emptyArray = (0, useStatic_1.useEmptyArray)();
    const renderUnitList_ = (0, react_1.useMemo)(() => (0, rxjs_1.combineLatest)([
        spuMeta_.pipe((0, rxjs_1.filter)(spuMeta => !!spuMeta.SpuCode), (0, rxjs_1.map)(compiler_1.renderUnitBuilderFactory)),
        paramList_.pipe((0, rxjs_1.map)(paramList => paramList.map(({ paramKey }) => paramKey))),
        strategyList_.pipe((0, rxjs_1.map)(({ buy }) => { var _a; return (_a = buy.at(0)) === null || _a === void 0 ? void 0 : _a.Description; })),
    ]).pipe((0, rxjs_1.skipUntil)(loaded_), (0, rxjs_1.map)(([renderUnitBuilder, paramKeyList, buyDescription]) => renderUnitBuilder(paramKeyList, buyDescription))), [spuMeta_, paramList_, strategyList_, loaded_]);
    const componentBuilder_ = (0, react_1.useMemo)(() => (0, rxjs_1.combineLatest)([paramList_, renderUnitList_]).pipe((0, rxjs_1.map)(([paramList, renderUnitList]) => (0, component_1.componentBuilderFactory)(paramList, renderUnitList))), [paramList_, renderUnitList_]);
    const componentList_ = (0, react_1.useMemo)(() => (0, rxjs_1.combineLatest)([values_, componentBuilder_]).pipe((0, rxjs_1.skipUntil)(loaded_), (0, rxjs_1.withLatestFrom)(validator_), (0, rxjs_1.map)(([[values, componentBuilder], validator]) => componentBuilder(validator.getEffectiveRules(values), values))), [componentBuilder_, validator_, values_, loaded_]);
    // 组件变化时动态修改表单值
    (0, react_1.useEffect)(() => {
        componentList_.pipe((0, rxjs_1.withLatestFrom)(values_)).subscribe({
            next: ([componentList, values]) => {
                var _a, _b;
                const calculableNameList = [];
                const defaultValues = {};
                for (const component of componentList) {
                    if (component.isCalculable) {
                        calculableNameList.push(component.name);
                    }
                    if (component.isField) {
                        const { valueType, defaultValue, defaultValueCalculator } = component;
                        let currentDefaultValue;
                        if (defaultValue !== undefined) {
                            currentDefaultValue = defaultValue;
                        }
                        else if (typeof defaultValueCalculator === 'function') {
                            currentDefaultValue = defaultValueCalculator(values);
                        }
                        if (currentDefaultValue !== undefined) {
                            switch (valueType) {
                                case spu_1.ValueTypeEnum.Int:
                                case spu_1.ValueTypeEnum.Decimal: {
                                    currentDefaultValue = Number(currentDefaultValue);
                                    break;
                                }
                                case spu_1.ValueTypeEnum.IntArray:
                                case spu_1.ValueTypeEnum.DecimalArray: {
                                    currentDefaultValue = (_a = currentDefaultValue.map) === null || _a === void 0 ? void 0 : _a.call(currentDefaultValue, Number);
                                    break;
                                }
                                case spu_1.ValueTypeEnum.String: {
                                    currentDefaultValue = String(currentDefaultValue);
                                    break;
                                }
                                case spu_1.ValueTypeEnum.StringArray: {
                                    currentDefaultValue = (_b = currentDefaultValue.map) === null || _b === void 0 ? void 0 : _b.call(currentDefaultValue, String);
                                    break;
                                }
                                case spu_1.ValueTypeEnum.Ref:
                                case spu_1.ValueTypeEnum.RefArray: {
                                    break;
                                }
                                default: {
                                    (0, assert_never_1.default)(valueType);
                                }
                            }
                            defaultValues[component.name] = currentDefaultValue;
                        }
                        else {
                            defaultValues[component.name] = undefined;
                        }
                    }
                }
                setParamBehavior(draft => {
                    // 删除参数中没有的字段
                    for (const { type, name, isCalculable } of componentList) {
                        if (draft[name] === undefined) {
                            draft[name] = {
                                isVisible: false,
                                isCalculable: false,
                            };
                        }
                        if (draft[name].isVisible !== (type !== component_2.ComponentType.Empty)) {
                            draft[name].isVisible = type !== component_2.ComponentType.Empty;
                        }
                        draft[name].isCalculable = isCalculable;
                    }
                });
                setDefaultValues(draft => {
                    for (const spec of calculableNameList) {
                        if (typeof defaultValues[spec] !== 'undefined') {
                            draft[spec] = defaultValues[spec];
                        }
                        else {
                            delete draft[spec];
                        }
                    }
                });
            },
        });
        // 设置静态表单值
        componentList_
            .pipe((0, rxjs_1.map)(componentList => componentList
            .filter(component => component.isField && (component.isStatic || component.isComputed))
            .map(({ name }) => name)), (0, rxjs_1.distinctUntilChanged)((prev, current) => prev.concat().sort().join() === current.concat().sort().join()))
            .subscribe({
            next: staticAndComputedFieldNameList => {
                setCurrentValues(draft => {
                    // 删除静态和借用字段
                    for (const name of Object.keys(draft)) {
                        if (staticAndComputedFieldNameList.includes(name)) {
                            delete draft[name];
                        }
                    }
                });
            },
        });
    }, [componentList_, setCurrentValues, setDefaultValues, setParamBehavior, values_]);
    const components = (0, useSubject_1.useStateWithObservable)(componentList_, emptyArray);
    return { components, componentList_ };
}
exports.useComponent = useComponent;
